import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Download, RefreshCw, AlertTriangle, CheckCircle2, Check, Lock, ArrowRightLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ars = (v) => `$${Number(v || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Renglón de una cuenta. `t` = tono: normal | gris | ok | mal | fuerte */
const Fila = ({ label, n, valor, t = 'normal', testid }) => {
  const tonos = {
    normal: 'text-gray-800 dark:text-gray-200',
    gris: 'text-gray-400 dark:text-gray-500',
    ok: 'text-emerald-700 dark:text-emerald-400',
    mal: 'text-red-600 dark:text-red-400 font-semibold',
    fuerte: 'text-gray-900 dark:text-white font-bold',
  };
  return (
    <div className={`flex items-baseline justify-between gap-2 py-1 ${t === 'fuerte' ? 'border-t border-gray-200 dark:border-gray-700 mt-1 pt-1.5' : ''}`}>
      <span className={`text-[11px] ${t === 'gris' ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}>
        {label}{n != null && <span className="ml-1 text-[10px] opacity-60">({n})</span>}
      </span>
      <span className={`text-[11px] font-mono tabular-nums whitespace-nowrap ${tonos[t]}`} data-testid={testid}>
        {ars(valor)}
      </span>
    </div>
  );
};

const CuentaCard = ({ c, onExport, exportando }) => {
  const { entradas: e, salidas: s, cargos_fijos: cf, cuadre: q, integridad_ledger: il } = c;
  const cuadra = Math.abs(q.diferencia) < 0.05;
  // Traspasos agrupados por cuenta destino: "375 → 309 (8)"
  const porDestino = useMemo(() => {
    const map = new Map();
    (c.traspasos_detalle || []).forEach((f) => {
      const k = f.destino_cvu_id || f.cuenta;
      const prev = map.get(k) || { n: 0, importe: 0, cvu: f.destino_cvu_id, alias: f.destino_alias };
      map.set(k, { ...prev, n: prev.n + 1, importe: prev.importe + f.importe });
    });
    return [...map.values()].sort((a, b) => b.importe - a.importe);
  }, [c.traspasos_detalle]);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
      data-testid={`resumen-cuenta-${c.cvu_id}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">CVU {c.cvu_id}</h3>
          <p className="text-[11px] text-gray-500 font-mono">{c.alias}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {cuadra
            ? <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold"><CheckCircle2 className="w-3 h-3" /> Cuadra</span>
            : <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold"><AlertTriangle className="w-3 h-3" /> {ars(q.diferencia)}</span>}
          <button onClick={() => onExport(c.cvu_id)} disabled={!c.exportable || exportando}
            className={`p-1.5 rounded-lg border transition-colors ${c.exportable
              ? 'border-blue-300 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'}`}
            title={c.exportable ? `Excel del CVU ${c.cvu_id}` : `Justificá las ${s.sin_justificar.n} salida(s) de este CVU`}
            data-testid={`resumen-export-cvu-${c.cvu_id}`}>
            {c.exportable ? <Download className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      <Fila label="Saldo al inicio" valor={c.saldo_inicio} t="fuerte" testid={`resumen-inicio-${c.cvu_id}`} />

      <p className="mt-3 mb-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">Entradas</p>
      <Fila label="Identificadas" n={e.identificadas.n} valor={e.identificadas.importe} t="ok" testid={`resumen-ent-ident-${c.cvu_id}`} />
      <Fila label="Pendientes de comprobante" n={e.pendientes.n} valor={e.pendientes.importe} t="gris" testid={`resumen-ent-pend-${c.cvu_id}`} />
      {e.fondeo_interno.n > 0 && <Fila label="Fondeo recibido de cuentas propias" n={e.fondeo_interno.n} valor={e.fondeo_interno.importe} t="gris" testid={`resumen-ent-fondeo-${c.cvu_id}`} />}
      <Fila label="Bruto entradas" valor={e.bruto_total} />
      <Fila label="Comisiones" valor={-e.comisiones} />
      <Fila label="Impuestos" valor={-e.impuestos} />
      <Fila label="Neto acreditado" valor={e.neto} t="fuerte" testid={`resumen-ent-neto-${c.cvu_id}`} />

      <p className="mt-3 mb-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700 dark:text-red-400">Salidas a terceros</p>
      <Fila label="Justificadas" n={s.justificadas.n} valor={s.justificadas.importe} testid={`resumen-sal-just-${c.cvu_id}`} />
      <Fila label="Sin justificar" n={s.sin_justificar.n} valor={s.sin_justificar.importe}
        t={s.sin_justificar.n > 0 ? 'mal' : 'gris'} testid={`resumen-sal-sinjust-${c.cvu_id}`} />
      <Fila label="Comisiones e impuestos" valor={-s.cargos_terceros} />
      <Fila label="Total a terceros" valor={s.terceros_total} t="fuerte" testid={`resumen-sal-terceros-${c.cvu_id}`} />

      <p className="mt-3 mb-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-700 dark:text-sky-400 flex items-center gap-1">
        <ArrowRightLeft className="w-3 h-3" /> Traspasos entre cuentas propias
      </p>
      {porDestino.length === 0
        ? <p className="text-[10px] text-gray-400 py-1">Sin traspasos en la semana</p>
        : porDestino.map((t) => (
          <Fila key={t.cvu || t.alias} label={`${c.cvu_id} → ${t.cvu || '?'}${t.alias ? ` · ${t.alias}` : ''}`}
            n={t.n} valor={t.importe} testid={`resumen-traspaso-${c.cvu_id}-${t.cvu || 'x'}`} />
        ))}
      {s.cargos_traspasos > 0 && <Fila label="Comisiones e impuestos" valor={-s.cargos_traspasos} />}
      <Fila label="Total traspasos" valor={s.traspasos_total} t="fuerte" testid={`resumen-traspasos-total-${c.cvu_id}`} />

      {cf.total > 0 && (
        <>
          <p className="mt-3 mb-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">Cargos 1TouchPay</p>
          {cf.servicio_api > 0 && <Fila label="Servicio API" valor={-cf.servicio_api} />}
          {cf.resumen_mensual > 0 && <Fila label="Resumen mensual" valor={-cf.resumen_mensual} />}
        </>
      )}

      <Fila label="Total egresado (terceros + traspasos)" valor={s.total_egresado} />
      <Fila label="Saldo al cierre" valor={c.saldo_cierre} t="fuerte" testid={`resumen-cierre-${c.cvu_id}`} />
      <Fila label="Variación de la semana" valor={c.variacion} t={c.variacion >= 0 ? 'ok' : 'mal'} />

      {Math.abs(il.diferencia) > 0.05 && (
        <p className="mt-2 text-[10px] text-amber-700 dark:text-amber-400 leading-snug" data-testid={`resumen-integridad-${c.cvu_id}`}>
          <AlertTriangle className="w-3 h-3 inline mr-0.5" />
          Integridad del ledger: {ars(il.diferencia)} de diferencia entre el saldo real y el histórico registrado.
        </p>
      )}
    </div>
  );
};

const BADGES = {
  justificada: ['Justificada', 'bg-emerald-100 text-emerald-800'],
  sin_justificar: ['Sin justificar', 'bg-red-100 text-red-800'],
  traspaso: ['Traspaso interno', 'bg-sky-100 text-sky-800'],
};

const SalidaFila = ({ mov, clientes, token, onListo }) => {
  const [nota, setNota] = useState(mov.nota || '');
  const [guardando, setGuardando] = useState(false);
  const [badge, clase] = BADGES[mov.estado] || BADGES.sin_justificar;

  const post = async (path, payload) => {
    setGuardando(true);
    try {
      const res = await fetch(`${API_URL}/api/bancos/1touchpay/ledger/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ otp_id: mov.otp_id, ...payload }),
      });
      if (res.ok) onListo?.();
    } catch (err) { console.error(err); }
    finally { setGuardando(false); }
  };

  const destino = mov.estado === 'traspaso'
    ? `${mov.cvu_id} → ${mov.destino_cvu_id || '?'}${mov.destino_alias ? ` · ${mov.destino_alias}` : ''}`
    : (mov.titular || '—');

  return (
    <tr className="border-b border-gray-100 dark:border-gray-700" data-testid={`resumen-salida-${mov.otp_id}`}>
      <td className="px-2 py-1.5 text-[10px] font-mono text-gray-500">{mov.cvu_id}</td>
      <td className="px-2 py-1.5 text-[10px] font-mono text-gray-600 dark:text-gray-400">{mov.created_at}</td>
      <td className="px-2 py-1.5 text-[11px] font-mono font-semibold text-red-600 text-right">{ars(mov.importe)}</td>
      <td className="px-2 py-1.5">
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${clase}`}
          data-testid={`resumen-estado-${mov.otp_id}`}>{badge}</span>
      </td>
      <td className="px-2 py-1.5 text-[10px] text-gray-700 dark:text-gray-300 max-w-[190px] truncate" title={`${destino} · ${mov.cuenta}`}>
        {destino}
      </td>
      <td className="px-2 py-1.5">
        {mov.estado === 'traspaso' ? (
          <span className="text-[10px] text-gray-400">cuenta propia</span>
        ) : mov.cliente_nombre ? (
          <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-medium">{mov.cliente_nombre}</span>
        ) : mov.sugerido_cliente_id ? (
          <button onClick={() => post('atribuir-cliente', { cliente_id: mov.sugerido_cliente_id })} disabled={guardando}
            className="text-[10px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50"
            data-testid={`resumen-confirmar-${mov.otp_id}`}>
            {guardando ? '…' : `Confirmar: ${mov.sugerido_nombre}`}
          </button>
        ) : (
          <select defaultValue="" disabled={guardando}
            onChange={(ev) => ev.target.value && post('atribuir-cliente', { cliente_id: ev.target.value })}
            className="text-[10px] px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 max-w-[150px]"
            data-testid={`resumen-select-${mov.otp_id}`}>
            <option value="">Asignar cliente…</option>
            {clientes.map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
          </select>
        )}
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1">
          <input value={nota} onChange={(ev) => setNota(ev.target.value)} maxLength={300}
            onKeyDown={(ev) => ev.key === 'Enter' && post('nota', { nota })}
            placeholder={mov.estado === 'traspaso' ? 'motivo del traspaso (opcional)' : '…o escribí el motivo'}
            className="text-[10px] px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 w-full"
            data-testid={`resumen-nota-${mov.otp_id}`} />
          <button onClick={() => post('nota', { nota })} disabled={guardando || nota === (mov.nota || '')}
            className="text-emerald-600 hover:text-emerald-800 disabled:opacity-30"
            data-testid={`resumen-nota-save-${mov.otp_id}`}><Check className="w-3.5 h-3.5" /></button>
        </div>
      </td>
    </tr>
  );
};

const FILTROS = [
  ['sin_justificar', 'Sin justificar'],
  ['terceros', 'A terceros'],
  ['traspasos', 'Traspasos'],
  ['todas', 'Todas'],
];

export default function ResumenEjecutivoTab() {
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [lunes, setLunes] = useState('');
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [expError, setExpError] = useState('');
  const [exportando, setExportando] = useState(false);
  const [filtro, setFiltro] = useState('sin_justificar');
  const [cvuFiltro, setCvuFiltro] = useState('todos');

  const fetchData = useCallback(async (semana) => {
    setLoading(true);
    try {
      const qs = semana ? `?lunes=${semana}` : '';
      const res = await fetch(`${API_URL}/api/bancos/1touchpay/resumen-semanal${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        if (!semana) setLunes(d.semana.lunes);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(lunes || null); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/bancos/options/clientes`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setClientes(await res.json());
      } catch (err) { console.error(err); }
    })();
  }, [token]);

  const exportar = async (cvu = null) => {
    setExpError('');
    setExportando(true);
    try {
      const qs = `?lunes=${lunes}${cvu ? `&cvu=${cvu}` : ''}`;
      const res = await fetch(`${API_URL}/api/bancos/1touchpay/resumen-semanal/export${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 409) { setExpError((await res.json()).detail); return; }
      if (!res.ok) { setExpError('No se pudo generar el Excel.'); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `resumen_semanal${cvu ? `_cvu${cvu}` : ''}_${lunes}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) { setExpError('No se pudo generar el Excel.'); }
    finally { setExportando(false); }
  };

  const salidas = useMemo(() => {
    if (!data) return [];
    return data.cuentas
      .filter((c) => cvuFiltro === 'todos' || String(c.cvu_id) === cvuFiltro)
      .flatMap((c) => (c.salidas_detalle || []).map((m) => ({ ...m, cvu_id: c.cvu_id })))
      .filter((m) => (
        filtro === 'todas' ? true
          : filtro === 'traspasos' ? m.estado === 'traspaso'
            : filtro === 'terceros' ? m.estado !== 'traspaso'
              : m.estado === 'sin_justificar'))
      .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  }, [data, filtro, cvuFiltro]);

  if (loading && !data) return <div className="py-16 text-center text-gray-500" data-testid="resumen-loading">Calculando el resumen…</div>;
  if (!data) return <div className="py-16 text-center text-gray-500">No se pudo cargar el resumen.</div>;

  return (
    <div className="space-y-4" data-testid="resumen-ejecutivo-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Resumen Ejecutivo Semanal</h2>
          <p className="text-xs text-gray-500">
            {data.semana.lunes} al {data.semana.domingo}
            {data.semana.en_curso && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold">EN CURSO · PARCIAL</span>}
          </p>
        </div>
        <select value={lunes} onChange={(ev) => { setLunes(ev.target.value); fetchData(ev.target.value); }}
          className="ml-auto px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          data-testid="resumen-selector-semana">
          {data.semanas_disponibles.map((s) => <option key={s.lunes} value={s.lunes}>{s.label}</option>)}
        </select>
        <button onClick={() => fetchData(lunes)} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          title="Recalcular" data-testid="resumen-refresh">
          <RefreshCw className={`w-4 h-4 text-gray-600 dark:text-gray-300 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button onClick={() => exportar(null)} disabled={!data.exportable || exportando}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            data.exportable ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
          title={data.exportable ? 'Excel con las 3 cuentas' : 'Primero justificá todas las salidas'}
          data-testid="resumen-export-btn">
          {data.exportable ? <Download className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
          Excel de las 3 cuentas
        </button>
      </div>

      {data.salidas_sin_justificar > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 px-4 py-3" data-testid="resumen-alerta-sinjustificar">
          <p className="text-sm font-semibold text-red-800 dark:text-red-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {data.salidas_sin_justificar} salida{data.salidas_sin_justificar > 1 ? 's' : ''} sin justificar
          </p>
          <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
            Cada CVU se exporta por separado en cuanto sus salidas estén completas. El Excel de las 3 cuentas espera a que no quede ninguna.
          </p>
        </div>
      )}
      {expError && <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-800" data-testid="resumen-export-error">{expError}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {data.cuentas.map((c) => <CuentaCard key={c.cvu_id} c={c} onExport={exportar} exportando={exportando} />)}
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2">
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Salidas de la semana</h3>
            <p className="text-[11px] text-gray-500">Asigná el cliente o escribí el motivo. Cada una que completes desbloquea su CVU.</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <select value={cvuFiltro} onChange={(ev) => setCvuFiltro(ev.target.value)}
              className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              data-testid="resumen-filtro-cvu">
              <option value="todos">Todos los CVU</option>
              {data.cuentas.map((c) => <option key={c.cvu_id} value={String(c.cvu_id)}>CVU {c.cvu_id}</option>)}
            </select>
            {FILTROS.map(([k, label]) => (
              <button key={k} onClick={() => setFiltro(k)}
                className={`text-[11px] px-2.5 py-1 rounded-lg font-medium transition-colors ${filtro === k
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                data-testid={`resumen-filtro-${k}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr className="text-[10px] uppercase tracking-wide text-gray-500">
                <th className="px-2 py-2 text-left">CVU</th>
                <th className="px-2 py-2 text-left">Fecha</th>
                <th className="px-2 py-2 text-right">Importe</th>
                <th className="px-2 py-2 text-left">Estado</th>
                <th className="px-2 py-2 text-left">Destino</th>
                <th className="px-2 py-2 text-left">Cliente</th>
                <th className="px-2 py-2 text-left">Motivo</th>
              </tr>
            </thead>
            <tbody data-testid="resumen-salidas-tbody">
              {salidas.map((m) => (
                <SalidaFila key={m.otp_id} mov={m} clientes={clientes} token={token}
                  onListo={() => fetchData(lunes)} />
              ))}
            </tbody>
          </table>
          {salidas.length === 0 && (
            <p className="py-8 text-center text-xs text-gray-400" data-testid="resumen-salidas-vacio">
              No hay salidas en este filtro.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
